import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Params, RouterModule } from '@angular/router';

import {
    ActionPreviewCardPayload,
    ActionPreviewChoiceSection,
    ActionPreviewDeepLinkSection,
    ActionPreviewEdits,
    ActionPreviewEmailTemplateSection,
    ActionPreviewGuardrailChipsSection,
    ActionPreviewHeaderSection,
    ActionPreviewImpactSummarySection,
    ActionPreviewInfoBlockSection,
    ActionPreviewKeyValueSection,
    ActionPreviewOutcomeSection,
    ActionPreviewRecipientsPeopleSection,
    ActionPreviewRecipientsSection,
    ActionPreviewSection,
    ActionPreviewStatusPickerSection,
    ActionPreviewTableSection,
    ActionPreviewToggleSection,
    ActionPreviewWhySection,
    GableViewerCapability,
} from '../../../../services/gable/gable.service';
import { RequestsRoutingService } from '../../../../services/requests/requests-routing.service';
import { CopyToClipboardDirective } from '../../shared/directives/button.directive';
import { ActionSummary, summarizeAction } from './action-preview-summarizers';
import { ActionPreviewHeaderSectionComponent } from './sections/header-section.component';
import { ActionPreviewEmailTemplateSectionComponent } from './sections/email-template-section.component';
import { ActionPreviewRecipientsSectionComponent } from './sections/recipients-section.component';
import { ActionPreviewRecipientsPeopleSectionComponent } from './sections/recipients-people-section.component';
import { ActionPreviewImpactSummarySectionComponent } from './sections/impact-summary-section.component';
import { ActionPreviewKeyValueSectionComponent } from './sections/key-value-section.component';
import { ActionPreviewInfoBlockSectionComponent } from './sections/info-block-section.component';
import { ActionPreviewTableSectionComponent } from './sections/table-section.component';
import { ActionPreviewStatusPickerSectionComponent } from './sections/status-picker-section.component';
import { ActionPreviewToggleSectionComponent } from './sections/toggle-section.component';
import { ActionPreviewDeepLinkSectionComponent } from './sections/deep-link-section.component';
import { ActionPreviewOutcomeSectionComponent } from './sections/outcome-section.component';
import { ActionPreviewWhySectionComponent } from './sections/why-section.component';
import { ActionPreviewGuardrailChipsSectionComponent } from './sections/guardrail-chips-section.component';
import { ActionPreviewChoiceSectionComponent } from './sections/choice-section.component';
import { TRANSACTIONS_ROUTE } from '../../../shared/accounting/transactions-routes';
import { DateTimeUtilsService } from '../../../../services/datetime-utils.service';
import moment from 'moment-timezone';

export type ActionPreviewCardState =
    | { kind: 'committed'; result: Record<string, unknown> }
    | { kind: 'cancelled' }
    | { kind: 'skipped' }
    | { kind: 'expired' }
    | { kind: 'superseded' };

/**
 * Host component for the AI action-preview card. Renders a stack of
 * typed sections (header / email_template / recipients / impact_summary
 * / ...) emitted by the backend's `propose_action` tool, accumulates
 * in-flight edits in `pendingEdits`, and routes utility-action and
 * primary/destructive button clicks back to the parent.
 *
 * Phase 2 lands the host + header + email-template sections. Recipients
 * + impact_summary + key_value + info_block + table sections land in
 * Phase 3. Unknown section kinds fall through to a small notice so the
 * chat doesn't break when the backend ships a new section before the UI
 * picks it up.
 */
@Component({
    selector: 'app-gable-action-preview-card',
    templateUrl: './action-preview-card.component.html',
    styleUrls: ['./action-preview-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CommonModule,
        RouterModule,
        ActionPreviewHeaderSectionComponent,
        ActionPreviewStatusPickerSectionComponent,
        ActionPreviewToggleSectionComponent,
        ActionPreviewEmailTemplateSectionComponent,
        ActionPreviewRecipientsSectionComponent,
        ActionPreviewRecipientsPeopleSectionComponent,
        ActionPreviewImpactSummarySectionComponent,
        ActionPreviewKeyValueSectionComponent,
        ActionPreviewInfoBlockSectionComponent,
        ActionPreviewTableSectionComponent,
        ActionPreviewDeepLinkSectionComponent,
        ActionPreviewOutcomeSectionComponent,
        ActionPreviewWhySectionComponent,
        ActionPreviewGuardrailChipsSectionComponent,
        ActionPreviewChoiceSectionComponent,
        CopyToClipboardDirective,
    ],
})
export class GableActionPreviewCardComponent implements OnChanges {
    @Input({ required: true }) payload!: ActionPreviewCardPayload;

    @Input() pmcId: number | null = null;

    /** Emitted when the user confirms the primary action. Parent owns the network call. */
    @Output() readonly confirm = new EventEmitter<{ proposalId: number; edits: ActionPreviewEdits }>();

    /** Emitted when the user clicks the destructive (Cancel / Don't create) action. */
    @Output() readonly cancel = new EventEmitter<{ proposalId: number }>();

    /** Emitted when the user clicks a utility action ("Soften tone", "Add unit", ...). */
    @Output() readonly utilityAction = new EventEmitter<{ proposalId: number; utilityActionId: string; edits: ActionPreviewEdits }>();

    /** Emitted when the user snoozes the proposal until tomorrow. Parent owns the network call. */
    @Output() readonly snooze = new EventEmitter<{ proposalId: number }>();

    /**
     * Accordion context (Activity queue): the collapsed row above the card
     * already renders title + summary, so the card drops its own `header`
     * section to avoid the duplicate. Chat cards keep the header.
     */
    @Input() hideHeaderSection = false;
    @Input() hideDeepLink = false;
    /** Another control runs the proposal (the inbox's Send), so the card drops its own buttons. */
    @Input() hideActions = false;

    /**
     * Server's verdict on whether THIS viewer can perform the action, and who
     * can if not. Null for actions with no per-user gate — which is nearly all of them,
     * so the default path is unchanged.
     *
     * The card is deliberately still rendered when this says no: the suggestion is useful
     * information ("this request is waiting on approval") even to someone who cannot act
     * on it. What changes is that the control is disabled and the reason is stated, rather
     * than offering a live button that can only fail.
     */
    @Input() viewerCapability: GableViewerCapability | null = null;

    get viewerCannotPerform(): boolean {
        return this.viewerCapability !== null && !this.viewerCapability.canPerform;
    }

    /** "Dana Reyes and 2 others must approve" — names the people who can act. */
    get viewerCannotPerformDetail(): string {
        const who = this.viewerCapability?.whoCan ?? [];
        if (who.length === 0) {
            return '';
        }
        if (who.length === 1) {
            return `${who[0]} must approve this request.`;
        }
        const others = who.length - 1;
        return `${who[0]} and ${others} other${others === 1 ? '' : 's'} must approve this request.`;
    }

    pendingEdits: ActionPreviewEdits = {};
    submitting = false;
    submitError: string | null = null;
    /** Separate from `submitting` so snoozing doesn't flip the confirm button to its "Creating…" spinner. */
    snoozing = false;
    finalState: ActionPreviewCardState | null = null;

    constructor(
            private _cdr: ChangeDetectorRef,
            private _requestsRouting: RequestsRoutingService,
            private _dateTimeUtils: DateTimeUtilsService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['payload']) {
            // Only a fresh/refined PENDING preview discards local edits — the
            // server bundle is the source of truth after a refine round-trip.
            // A commit/cancel state patch (committing/committed/cancelled) must
            // KEEP the edits: otherwise the count/impact flash to zero for a
            // frame while the committed view paints (the body is still visible
            // during 'committing').
            if ((this.payload?.state ?? 'pending') === 'pending') {
                this.pendingEdits = {};
                this.submitting = false;
                this.snoozing = false;
            }

            // Resilient final state: if the host patched `payload.state` to
            // 'committed' or 'cancelled' (e.g. after a successful reject /
            // commit), reflect that here even if `setFinalState()` wasn't
            // called separately. This makes the "Action cancelled" / "Action
            // confirmed" UI survive any ViewChildren lookup race. Result
            // (from the execute() envelope) is mirrored onto the payload
            // by `patchActionPreviewState` so the collapsed card can read
            // unit_count / unit_ids for its label + deeplink.
            const rawPayload = this.payload as unknown as Record<string, unknown> | null;
            if (this.payload?.state === 'committed') {
                const result = (rawPayload?.['result'] as Record<string, unknown> | undefined) ?? {};
                this.finalState = { kind: 'committed', result };
            } else if (this.payload?.state === 'cancelled') {
                this.finalState = { kind: 'cancelled' };
            } else if (this.payload?.state === 'skipped') {
                this.finalState = { kind: 'skipped' };
            } else if (this.payload?.state === 'expired') {
                this.finalState = { kind: 'expired' };
            } else if (this.payload?.state === 'superseded') {
                this.finalState = { kind: 'superseded' };
            } else {
                this.finalState = null;
            }
        }
    }

    get sections(): ActionPreviewSection[] {
        return this.payload?.preview?.sections ?? [];
    }

    /**
     * Sections rendered in the post-commit (Approved) view: drop the
     * `header` section (the card chrome renders it inline so we can swap
     * in the green "Approved" badge) and the `status_picker` (the user
     * shouldn't be able to change status after the action's already
     * fired). Everything else — email_template, recipients, summary —
     * stays visible as a read-back of what was sent.
     */
    get postCommitSections(): ActionPreviewSection[] {
        return this.sections.filter(
            (s) => s.kind !== 'header' && s.kind !== 'status_picker' && s.kind !== 'toggle' && s.kind !== 'choice',
        );
    }

    /**
     * Title text from the `header` section, surfaced separately so the
     * card chrome can render it alongside the state badge in both the
     * pending and committed views.
     */
    get headerTitle(): string {
        const header = this.sections.find((s) => s.kind === 'header');
        return header?.kind === 'header' ? header.title : '';
    }

    get headerSubtitle(): string {
        const header = this.sections.find((s) => s.kind === 'header');
        return header?.kind === 'header' ? (header.subtitle ?? '') : '';
    }

    /**
     * Confidence tier for the card, extracted from the first `why` section
     * (A4/A5). Passed down to the outcome / choice decision-surface headers so
     * the badge renders there instead of inside the `why` block.
     */
    get confidence(): 'high' | 'review' | 'low' | null {
        const why = this.sections.find((s) => s.kind === 'why');
        return why?.kind === 'why' ? (why.confidence ?? null) : null;
    }

    /**
     * Record string for the hoisted context row (A2) — the affected-record
     * text from the first `outcome` section. Rendered once at the top of the
     * pending body so the outcome panel doesn't repeat it.
     */
    get communityName(): string | null {
        const payload = this.payload as (ActionPreviewCardPayload & { organizationName?: string }) | null;
        return payload?.organization_name ?? payload?.organizationName ?? null;
    }

    get contextRecord(): string | null {
        const outcome = this.sections.find((s) => s.kind === 'outcome');
        return outcome?.kind === 'outcome' ? (outcome.record ?? null) : null;
    }

    /**
     * Deep link for the hoisted context row (A2) — the first `deep_link`
     * section. When present it renders as the right-aligned link in the context
     * row and the standalone `deep_link` section renders nothing.
     */
    get contextLink(): ActionPreviewDeepLinkSection | null {
        if (this.hideDeepLink) {
            return null;
        }
        const link = this.sections.find((s) => s.kind === 'deep_link');
        if (link?.kind !== 'deep_link') {
            return null;
        }
        return { ...link, route: this.normalizeDeepLinkRoute(link.route) };
    }

    /**
     * Legacy proposals baked the dead `/app/requests/{id}` route before the canonical
     * Requests detail paths landed. Rewrite the bare numeric form so stale queue items
     * don't 404 after deploy — through RequestsRoutingService, so a V3 org gets the V3
     * detail rather than being dropped onto the V2 page. Newer proposals already emit a
     * correct route and pass through untouched.
     */
    private normalizeDeepLinkRoute(route: string): string {
        if (this.pmcId !== null) {
            const pmcRoute = route
                .replace(/^\/app\/requests\/submission\/(\d+)$/, `/pmc/${this.pmcId}/requests/submissions/$1/details`)
                .replace(/^\/app\/requests\/(\d+)$/, `/pmc/${this.pmcId}/requests/submissions/$1/details`)
                .replace(/^\/app\/violations\/(?:detail\/)?(\d+)$/, `/pmc/${this.pmcId}/violations/$1/details`);
            if (pmcRoute !== route) {
                return pmcRoute;
            }
        }
        return route.replace(
            /^\/app\/requests\/(\d+)$/,
            (_match, id: string) => this._requestsRouting.submissionDetail(id),
        );
    }

    /** True when the hoisted context row (record and/or deep link) should show. */
    get contextRowVisible(): boolean {
        return this.contextRecord !== null || this.contextLink !== null;
    }

    /**
     * Does the body have anything to show?
     *
     * `.ap-card-content` carries its own 16px padding and a top border, so when an action has no
     * preview sections AND no context row — which is the normal case for a simple approve, and
     * doubly so once the context row is suppressed as duplicating the title — the div still
     * painted 32px of padding under a divider. An empty box drawing its own decoration, which
     * read as a rendering fault in the middle of the card.
     */
    get hasBodyContent(): boolean {
        return this.contextRowVisible || this.bodySections.length > 0;
    }

    /**
     * Live outcome text: when the user changes the status_picker value, the
     * "If you confirm" effect line re-renders with the newly chosen status
     * label swapped in ("1 request moves to Under Review." → "…to Denied.").
     * The section components already push edits UP to `pendingEdits` (the
     * OnPush-friendly version of two-way binding); this getter closes the
     * loop back DOWN into the outcome surface. Null → server text stands.
     */
    get outcomeEffectOverride(): string | null {
        const editVal = this.pendingEdits.status_picker?.value;
        if (!editVal) {
            return null;
        }
        const picker = this.sections.find((s) => s.kind === 'status_picker');
        const outcome = this.sections.find((s) => s.kind === 'outcome');
        if (picker?.kind !== 'status_picker' || outcome?.kind !== 'outcome') {
            return null;
        }
        const oldLabel = picker.options.find((o) => o.value === picker.value)?.label;
        const newLabel = picker.options.find((o) => o.value === editVal)?.label;
        if (!oldLabel || !newLabel || oldLabel === newLabel || !outcome.effect.includes(oldLabel)) {
            return null;
        }
        return outcome.effect.replace(oldLabel, newLabel);
    }

    /**
     * Pending-body sections in Figma display order: the `why` rationale block
     * hoists directly under the context row, BEFORE the decision surface —
     * the server still emits it near the end of the list. Cards without a
     * `why` section (chat-era previews) keep their server order untouched.
     * With `hideHeaderSection` (accordion context) the header section drops —
     * the collapsed row above the card already shows title + summary.
     */
    get bodySections(): ActionPreviewSection[] {
        const all = this.sections;
        const why = all.filter((s) => s.kind === 'why');
        let ordered = why.length > 0
            ? [...why, ...all.filter((s) => s.kind !== 'why')]
            : all;
        // The context row already names the affected record — a key_value
        // section that repeats ONLY that same string is pure duplication
        // (Figma shows the record once, up top). Prefix-tolerant so a value
        // with a trailing "· status: …" suffix still counts as a repeat.
        // Multi-record lists stay. Likewise, when the outcome decision
        // surface is present it IS the impact statement — the legacy
        // impact_summary section would say the same thing twice.
        const record = this.contextRecord;
        const hasOutcome = all.some((s) => s.kind === 'outcome');
        const repeatsRecord = (value: string): boolean =>
            record !== null
            && (value === record || value.startsWith(record) || record.startsWith(value));
        ordered = ordered.filter((s) => {
            if (s.kind === 'key_value' && s.items.length > 0
                && s.items.every((item) => repeatsRecord(item.value))) {
                return false;
            }
            if (s.kind === 'info_block' && repeatsRecord(s.body)) {
                return false;
            }
            // Suppressed kinds must not leave empty .ap-section wrappers
            // behind — each one still counts as a flex child and adds a
            // phantom 16px gap slot in the body column.
            if (s.kind === 'guardrail_chips') {
                return false;
            }
            if (s.kind === 'deep_link' && (this.hideDeepLink || this.contextRowVisible)) {
                return false;
            }
            return !(hasOutcome && s.kind === 'impact_summary');
        });
        return this.hideHeaderSection
            ? ordered.filter((s) => s.kind !== 'header')
            : ordered;
    }

    trackBySectionIndex(index: number, _section: ActionPreviewSection): number {
        return index;
    }

    asHeader(s: ActionPreviewSection): ActionPreviewHeaderSection | null {
        return s.kind === 'header' ? s : null;
    }

    asStatusPicker(s: ActionPreviewSection): ActionPreviewStatusPickerSection | null {
        return s.kind === 'status_picker' ? s : null;
    }

    asEmail(s: ActionPreviewSection): ActionPreviewEmailTemplateSection | null {
        return s.kind === 'email_template' ? s : null;
    }

    asRecipients(s: ActionPreviewSection): ActionPreviewRecipientsSection | null {
        return s.kind === 'recipients' ? s : null;
    }

    asImpactSummary(s: ActionPreviewSection): ActionPreviewImpactSummarySection | null {
        return s.kind === 'impact_summary' ? s : null;
    }

    asKeyValue(s: ActionPreviewSection): ActionPreviewKeyValueSection | null {
        return s.kind === 'key_value' ? s : null;
    }

    asInfoBlock(s: ActionPreviewSection): ActionPreviewInfoBlockSection | null {
        return s.kind === 'info_block' ? s : null;
    }

    asTable(s: ActionPreviewSection): ActionPreviewTableSection | null {
        return s.kind === 'table' ? s : null;
    }

    asToggle(s: ActionPreviewSection): ActionPreviewToggleSection | null {
        return s.kind === 'toggle' ? s : null;
    }

    /**
     * The card's recipients-style section, whichever variant the backend
     * sent: `recipients` (units) or `recipients_people` (people picker).
     * Both share ActionPreviewRecipientItem[] and the same edit envelope.
     */
    private get _recipientsSection(): ActionPreviewRecipientsSection | ActionPreviewRecipientsPeopleSection | null {
        const section = this.sections.find(
            (s): s is ActionPreviewRecipientsSection | ActionPreviewRecipientsPeopleSection =>
                s.kind === 'recipients' || s.kind === 'recipients_people',
        );
        return section ?? null;
    }

    /** Effective recipient count: base items (minus removed) plus newly added — governs the button label + disabled state for both recipients variants. */
    get effectiveRecipientCount(): number {
        const section = this._recipientsSection;
        if (!section) {
            return 0;
        }
        const removed = new Set(this.pendingEdits.recipients?.removed_ids ?? []);
        // Removals only target rendered rows, so tally those then subtract from the TRUE total (totalCount), not items.length.
        const removedVisibleCount = section.items.filter(
            (i) => i.removed === true || removed.has(i.id),
        ).length;
        const base = section.totalCount ?? section.items.length;
        const activeBase = Math.max(base - removedVisibleCount, 0);
        const added = this.pendingEdits.recipients?.added_ids?.length ?? 0;
        return activeBase + added;
    }

    /** Muted scale line under the card title ("194 units") — only for previews carrying a numeric `totalCount`; null otherwise so ordinary cards render nothing. */
    get scaleLine(): string | null {
        const section = this._recipientsSection;
        if (!section || typeof section.totalCount !== 'number' || section.totalCount <= 0) {
            return null;
        }
        const noun = section.targetNoun ?? (section.kind === 'recipients_people' ? 'recipients' : 'units');
        return `${section.totalCount} ${noun}`;
    }

    /**
     * The card's `choice` section (the low-confidence select that gates the
     * primary confirm), or null when the preview has none.
     */
    private get _choiceSection(): ActionPreviewChoiceSection | null {
        const section = this.sections.find(
            (s): s is ActionPreviewChoiceSection => s.kind === 'choice',
        );
        return section ?? null;
    }

    /**
     * True when a `choice` section exists, Gable couldn't decide
     * (`suggested === null`), and the user hasn't picked yet. Per contract §1
     * this blocks the primary confirm until a value is chosen.
     */
    get choiceBlocked(): boolean {
        const section = this._choiceSection;
        if (section === null || section.suggested !== null) {
            return false;
        }
        return !this.pendingEdits.choice?.value;
    }

    /**
     * Primary button is disabled when the recipients section has zero
     * actively-selected items, or when an unresolved `choice` section gates
     * confirm. Counts base items minus removed plus newly added — so picking
     * units in the empty-proposal flow enables the button.
     */
    get isPrimaryDisabled(): boolean {
        // The viewer holds no right to perform this action (e.g. an Approve
        // suggestion shown to someone who is not a designated approver on that form).
        // Checked first: no amount of editing the card makes it actionable for them.
        if (this.viewerCannotPerform) {
            return true;
        }
        if (this.choiceBlocked) {
            return true;
        }
        if (this._recipientsSection === null) {
            return false;
        }
        return this.effectiveRecipientCount === 0;
    }

    /** Tooltip on the disabled primary button, phrased for the blocking cause. */
    get primaryDisabledReason(): string {
        if (this.viewerCannotPerform) {
            return this.viewerCapability?.reason ?? 'You cannot perform this action.';
        }
        if (this.choiceBlocked) {
            return 'Choose an option first';
        }
        return this._recipientsSection?.kind === 'recipients_people'
            ? 'Add at least one recipient first'
            : 'Pick at least one unit first';
    }

    /**
     * Live impact + button-label text. All per-action wording lives in the
     * declarative summarizer registry (action-preview-summarizers.ts) — the
     * card just supplies current state, so no per-type branches grow here.
     */
    private get _summary(): ActionSummary {
        const impact = this.sections.find((s) => s.kind === 'impact_summary');
        const impactSection = impact?.kind === 'impact_summary' ? impact : null;
        return summarizeAction(this._actionType, {
            count: this.effectiveRecipientCount,
            hasPicker: this._recipientsSection !== null,
            peoplePicker: this._recipientsSection?.kind === 'recipients_people',
            toggles: this.pendingEdits.toggles ?? {},
            serverButtonLabel: this.payload?.preview?.actions?.primary?.label ?? null,
            // Middleware camelCases the nested preview payload — read camel first,
            // snake as the fixture/back-compat fallback (Brian P2).
            labelTemplate: this.payload?.preview?.actions?.primary?.labelTemplate
                ?? this.payload?.preview?.actions?.primary?.label_template ?? null,
            impactTemplate: impactSection?.primaryTemplate ?? impactSection?.primary_template ?? null,
            perUnitCents: impactSection?.perUnitCents ?? impactSection?.per_unit_cents ?? null,
        });
    }

    /** Impact-line override; null → keep the server's static text. */
    get impactSummaryPrimaryOverride(): string | null {
        return this._summary.impactPrimary;
    }

    /** Primary button label — summarizer override, else the server label. */
    get primaryButtonLabel(): string {
        return this._summary.buttonLabel ?? this.payload?.preview?.actions?.primary?.label ?? 'Confirm';
    }

    toggleValue(key: string): boolean | null {
        const v = this.pendingEdits.toggles?.[key];
        return typeof v === 'boolean' ? v : null;
    }

    isKnownKind(s: ActionPreviewSection): boolean {
        return (
            s.kind === 'header'
            || s.kind === 'status_picker'
            || s.kind === 'toggle'
            || s.kind === 'email_template'
            || s.kind === 'recipients'
            || s.kind === 'recipients_people'
            || s.kind === 'impact_summary'
            || s.kind === 'key_value'
            || s.kind === 'info_block'
            || s.kind === 'table'
            || s.kind === 'deep_link'
            || s.kind === 'outcome'
            || s.kind === 'why'
            || s.kind === 'guardrail_chips'
            || s.kind === 'choice'
        );
    }

    onToggleEdit(edit: { key: string; value: boolean }): void {
        this.pendingEdits = {
            ...this.pendingEdits,
            toggles: { ...this.pendingEdits.toggles, [edit.key]: edit.value },
        };
        this._cdr.markForCheck();
    }

    onStatusEdit(edit: NonNullable<ActionPreviewEdits['status_picker']>): void {
        this.pendingEdits = {
            ...this.pendingEdits,
            status_picker: { ...this.pendingEdits.status_picker, ...edit },
        };
        this._cdr.markForCheck();
    }

    onChoiceEdit(edit: NonNullable<ActionPreviewEdits['choice']>): void {
        this.pendingEdits = {
            ...this.pendingEdits,
            choice: { ...this.pendingEdits.choice, ...edit },
        };
        this._cdr.markForCheck();
    }

    onEmailEdit(edit: NonNullable<ActionPreviewEdits['email_template']>): void {
        this.pendingEdits = {
            ...this.pendingEdits,
            email_template: { ...this.pendingEdits.email_template, ...edit },
        };
        this._cdr.markForCheck();
    }

    onRecipientsEdit(edit: NonNullable<ActionPreviewEdits['recipients']>): void {
        this.pendingEdits = {
            ...this.pendingEdits,
            recipients: { ...this.pendingEdits.recipients, ...edit },
        };
        this._cdr.markForCheck();
    }

    /**
     * Recipients-level list actions ("Restore skipped", "Add unit") flow
     * through the same utility-action emitter as the card's footer
     * utility buttons — the server resolves the id regardless of origin.
     */
    onRecipientsListAction(id: string): void {
        this.onUtilityClick(id);
    }

    onUtilityClick(id: string): void {
        if (this.submitting) {
            return;
        }
        this.utilityAction.emit({
            proposalId: this.resolvedProposalId,
            utilityActionId: id,
            edits: this.pendingEdits,
        });
    }

    onConfirm(): void {
        // Guard against programmatic/raced clicks too — the [disabled]
        // binding alone isn't a guarantee (e.g. Enter-key activation while
        // the binding hasn't re-evaluated).
        if (this.submitting || this.isPrimaryDisabled) {
            return;
        }
        this.submitting = true;
        this.submitError = null;
        this._cdr.markForCheck();
        this.confirm.emit({
            proposalId: this.resolvedProposalId,
            edits: this.pendingEdits,
        });
    }

    onCancel(): void {
        if (this.submitting || this.snoozing) {
            return;
        }
        this.cancel.emit({ proposalId: this.resolvedProposalId });
    }

    /** Only offer snooze where a parent handles it; chat never did, so its button hung on "Snoozing…". */
    get canSnooze(): boolean {
        return this.snooze.observed;
    }

    onSnooze(): void {
        if (this.submitting || this.snoozing) {
            return;
        }
        // Lock the row via a DEDICATED flag — reusing `submitting` made the
        // primary confirm button flip to its "Creating…" spinner, which read
        // as if snooze were creating something. The parent resets us via a
        // payload patch if the snooze fails.
        this.snoozing = true;
        this.snooze.emit({ proposalId: this.resolvedProposalId });
    }

    /**
     * Read the proposal id from whichever casing the wire carries. The
     * response transformer middleware camelCases response keys at the
     * top level AND recurses into nested objects — so a payload that was
     * `proposal_id` on the API side arrives here as `proposalId`. The
     * `isActionPreviewCardPayload` guard already accepts both spellings;
     * mirror that here so the commit/refine round-trip doesn't end up
     * POSTing to `.../proposals/undefined/commit-action`.
     */
    private get resolvedProposalId(): number {
        const raw = this.payload as unknown as Record<string, unknown> | null;
        const direct = raw?.['proposal_id'];
        if (typeof direct === 'number') {
            return direct;
        }
        const camel = raw?.['proposalId'];
        if (typeof camel === 'number') {
            return camel;
        }
        return 0;
    }

    /**
     * The vendor secure link `manage_task_vendor_access` issues on commit. Only ever read off
     * the live commit HTTP response (`_executedResult`) — core does not let the model repeat it
     * and the card carries no other copy of it, so a reload of this card loses it for good.
     */
    get vendorAccessLink(): { url: string; expiresAtLabel: string | null } | null {
        if (this._actionType !== 'manage_task_vendor_access') {
            return null;
        }
        const result = this._executedResult();
        if ((result['url_issued'] ?? result['urlIssued']) !== true) {
            return null;
        }
        const url = result['url'];
        if (typeof url !== 'string' || url === '') {
            return null;
        }
        const expiresAt = result['expires_at'] ?? result['expiresAt'];
        const expiresAtLabel = typeof expiresAt === 'string' && expiresAt !== ''
            ? this._formatVendorLinkExpiry(expiresAt)
            : null;
        return { url, expiresAtLabel };
    }

    private _formatVendorLinkExpiry(iso: string): string | null {
        const m = moment.tz(iso, this._dateTimeUtils.getOrgTimezone());
        return m.isValid() ? m.format('MMM D, YYYY h:mm A z') : null;
    }

    /**
     * Label shown alongside the green check in the collapsed
     * post-commit row. Falls back to a generic confirmation when the
     * action type isn't in the mapping below.
     */
    get completedLabel(): string {
        if (this._actionType === 'send_violation_notice') {
            const result = this._executedResult();
            const created = this._readNumber(result, ['created_count', 'createdCount'])
                ?? this._executedUnitCount();
            const emailSent = result['email_sent'] ?? result['emailSent'];
            const recipientCount = this._readNumber(result, ['email_recipient_count', 'emailRecipientCount']) ?? 0;
            if (created <= 0) {
                return 'Violations created.';
            }
            return emailSent === true
                ? `Created ${created} violations · emailed ${recipientCount} homeowners.`
                : `Created ${created} violations · no email sent.`;
        }
        if (this._actionType === 'set_request_status') {
            const result = this._executedResult();
            const count = this._readNumber(result, ['submission_count', 'submissionCount']) ?? 0;
            const status = result['status_title'] ?? result['statusTitle'];
            return typeof status === 'string' && count > 0
                ? `Moved ${count} request${count === 1 ? '' : 's'} to ${status}.`
                : 'Request status updated.';
        }
        if (this._actionType === 'reply_to_request') {
            const result = this._executedResult();
            return result['email_sent'] === true
                ? 'Reply posted and emailed to the submitter.'
                : 'Reply posted to the request.';
        }
        if (this._actionType === 'categorize_transaction') {
            const result = this._executedResult();
            const done = this._readNumber(result, ['categorized_count', 'categorizedCount']) ?? 0;
            const skipped = this._readNumber(result, ['skipped_count', 'skippedCount']) ?? 0;
            return skipped > 0
                ? `Categorized ${done} transaction${done === 1 ? '' : 's'} · ${skipped} skipped.`
                : `Categorized ${done} transaction${done === 1 ? '' : 's'}.`;
        }
        if (this._actionType === 'send_payment_reminder') {
            const result = this._executedResult();
            const n = this._readNumber(result, ['email_recipient_count', 'emailRecipientCount']) ?? 0;
            return n > 0
                ? `Payment reminder sent to ${n} homeowner${n === 1 ? '' : 's'}.`
                : 'Payment reminder sent.';
        }
        if (this._actionType === 'email_unit_owners') {
            const result = this._executedResult();
            const n = this._readNumber(result, ['email_recipient_count', 'emailRecipientCount']) ?? 0;
            return n > 0
                ? `Announcement emailed to ${n} homeowner${n === 1 ? '' : 's'}.`
                : 'Announcement sent.';
        }
        if (this._actionType === 'update_violation_status') {
            const result = this._executedResult();
            const count = this._readNumber(result, ['violation_count', 'violationCount']) ?? 0;
            const resolved = this._readNumber(result, ['resolved_count', 'resolvedCount']) ?? 0;
            const status = result['status_title'] ?? result['statusTitle'];
            if (resolved > 0 && typeof status !== 'string') {
                return `Resolved ${resolved} violation${resolved === 1 ? '' : 's'}.`;
            }
            return typeof status === 'string' && count > 0
                ? `Moved ${count} violation${count === 1 ? '' : 's'} to ${status}${resolved > 0 ? ` · ${resolved} resolved` : ''}.`
                : 'Violation status updated.';
        }
        if (this._actionType === 'approve_payable') {
            const result = this._executedResult();
            const summary = result['summary'];
            if (typeof summary === 'string' && summary.trim() !== '') {
                return summary;
            }
            const n = this._readNumber(result, ['approved_count', 'approvedCount']) ?? 0;
            const skipped = this._readNumber(result, ['skipped_count', 'skippedCount']) ?? 0;
            return n > 0
                ? `Approved ${n} payable${n === 1 ? '' : 's'}${skipped > 0 ? ` · ${skipped} skipped` : ''}.`
                : 'Payable approval recorded.';
        }
        if (this._actionType === 'create_workflow') {
            return 'Workflow created. Find it under Workflows.';
        }
        if (this._actionType === 'batch') {
            const result = this._executedResult();
            const appliedRaw = this._readNumber(result, ['applied_count', 'appliedCount']);
            if (appliedRaw === null) {
                return 'Changes applied.';
            }
            const skipped = this._readNumber(result, ['skipped_count', 'skippedCount']) ?? 0;
            const total = this._readNumber(result, ['row_count', 'rowCount']) ?? appliedRaw + skipped;
            return `Applied ${appliedRaw} of ${total} change${total === 1 ? '' : 's'}.${skipped > 0 ? ` ${skipped} skipped.` : ''}`;
        }
        return 'Action confirmed.';
    }

    /** One line per row a committed batch skipped: "Row 7 · Unit 12: reason". */
    get skippedLines(): string[] {
        if (this._actionType !== 'batch') {
            return [];
        }
        const raw = this._executedResult()['skipped'];
        if (raw === this._skippedSource) {
            return this._skippedLines;
        }
        this._skippedSource = raw;
        this._skippedLines = !Array.isArray(raw)
            ? []
            : raw
                .filter((r): r is { row?: unknown; label?: unknown; reason?: unknown } => r !== null && typeof r === 'object')
                .map((r) => {
                    const label = typeof r.label === 'string' && r.label !== '' ? ` · ${r.label}` : '';
                    const reason = typeof r.reason === 'string' && r.reason !== '' ? `: ${r.reason}` : '';
                    return `Row ${r.row ?? '?'}${label}${reason}`;
                });
        return this._skippedLines;
    }

    private _skippedSource: unknown = undefined;
    private _skippedLines: string[] = [];

    /** Route to deep-link to from the collapsed card, per action_type. */
    /**
     * Core resolves where the changed record lives and sends it on the commit result
     * (SD-9957). It knows the org's routing flags; the card only renders the answer.
     */
    get destinationRoute(): string | null {
        const route = this._recordLink()?.route;
        if (typeof route === 'string' && route !== '') {
            return route.split('?')[0];
        }

        return this._actionType === 'categorize_transaction' ? TRANSACTIONS_ROUTE : null;
    }

    /** A record link may carry a query string (e.g. ?templateId=), which routerLink needs as queryParams. */
    get destinationQuery(): Params | null {
        const route = this._recordLink()?.route;
        if (typeof route !== 'string' || !route.includes('?')) {
            return null;
        }
        const params: Params = {};
        new URLSearchParams(route.split('?')[1]).forEach((value, key) => { params[key] = value; });
        return params;
    }

    get destinationLabel(): string {
        const label = this._recordLink()?.label;

        return typeof label === 'string' && label !== '' ? label : 'View details';
    }

    /**
     * One link per record a committed bulk action touched (SD-10205). Core only sends
     * this when more than one record has its own page, so a single-record card keeps
     * its one "View details" link and renders exactly as before.
     */
    get recordLinks(): { label: string; route: string }[] {
        if (this.finalState?.kind !== 'committed') {
            return [];
        }
        const result = this._executedResult();
        const raw = result['record_links'] ?? result['recordLinks'];
        if (!Array.isArray(raw)) {
            return [];
        }
        return raw
            .filter((l): l is { label: string; route: string } =>
                l !== null && typeof l === 'object'
                && typeof (l as { route?: unknown }).route === 'string'
                && typeof (l as { label?: unknown }).label === 'string')
            .map((l) => ({ label: l.label, route: l.route }));
    }

    /** Bulk results carry a list; show it instead of the single fallback link. */
    get showRecordLinks(): boolean {
        return this.recordLinks.length > 1;
    }

    /** Links shown before "Show N more", matching how the recipients list truncates. */
    static readonly RECORD_LINKS_VISIBLE = 5;

    showAllRecordLinks = false;

    get visibleRecordLinks(): { label: string; route: string }[] {
        const links = this.recordLinks;
        return this.showAllRecordLinks ? links : links.slice(0, GableActionPreviewCardComponent.RECORD_LINKS_VISIBLE);
    }

    get hiddenRecordLinkCount(): number {
        return this.showAllRecordLinks
            ? 0
            : Math.max(0, this.recordLinks.length - GableActionPreviewCardComponent.RECORD_LINKS_VISIBLE);
    }

    onShowAllRecordLinks(): void {
        this.showAllRecordLinks = true;
    }

    trackByRecordRoute = (_: number, link: { route: string }): string => link.route;

    trackBySkippedLine = (_: number, line: string): string => line;

    /**
     * The committed card links to what changed; the preview links to what is about to.
     * Core sends the same shape on both, so prefer the executed result and fall back to
     * the proposal payload. Dual-read because the response transformer camelCases
     * nested payload keys, same as `action_type`.
     */
    private _recordLink(): { label?: string; route?: string } | null {
        const result = this._executedResult() as Record<string, unknown> | null;
        const payload = this.payload as unknown as Record<string, unknown> | null;
        // A card re-read from history carries the link on the frozen preview, not the result.
        const preview = (payload?.['preview'] ?? null) as Record<string, unknown> | null;
        const link = result?.['record_link'] ?? result?.['recordLink']
            ?? payload?.['record_link'] ?? payload?.['recordLink']
            ?? preview?.['record_link'] ?? preview?.['recordLink'];

        if (link !== null && typeof link === 'object') {
            return link as { label?: string; route?: string };
        }

        // A list with a single entry is still a destination; never show nothing.
        const only = this.recordLinks.length === 1 ? this.recordLinks[0] : null;
        return only;
    }

    /**
     * Read `action_type` from the payload tolerating either casing. The
     * response transformer middleware camelCases nested toolCard payload
     * keys, so `action_type` (server-emitted) often arrives as `actionType`
     * by the time it hits this component. Mirrors the same dual-read
     * pattern used for `proposal_id` / `proposalId` on the click handlers.
     */
    private get _actionType(): string | undefined {
        const raw = this.payload as unknown as Record<string, unknown> | null;
        const snake = raw?.['action_type'];
        if (typeof snake === 'string') {
            return snake;
        }
        const camel = raw?.['actionType'];
        if (typeof camel === 'string') {
            return camel;
        }
        return undefined;
    }

    private _executedResult(): Record<string, unknown> {
        if (this.finalState?.kind !== 'committed') {
            return {};
        }
        return this.finalState.result as Record<string, unknown>;
    }

    private _readNumber(result: Record<string, unknown>, keys: string[]): number | null {
        for (const k of keys) {
            const v = result[k];
            if (typeof v === 'number') {
                return v;
            }
        }
        return null;
    }

    private _executedUnitCount(): number {
        const result = this._executedResult();
        const unitCount = result['unit_count'] ?? result['unitCount'];
        if (typeof unitCount === 'number') {
            return unitCount;
        }
        const unitIds = result['unit_ids'] ?? result['unitIds'];
        return Array.isArray(unitIds) ? unitIds.length : 0;
    }

    /**
     * Called by the parent once the network round-trip resolves so the
     * card can show its final disabled state inline instead of vanishing.
     */
    resetBusy(errorMessage: string | null = null): void {
        this.submitting = false;
        this.snoozing = false;
        this.submitError = errorMessage;
        this._cdr.markForCheck();
    }

    setFinalState(state: ActionPreviewCardState): void {
        this.finalState = state;
        this.submitting = false;
        this._cdr.markForCheck();
    }
}
